import { HStack } from "@chakra-ui/react";
import { format, isValid, parse } from "date-fns";
import { FormikValues, useFormikContext } from "formik";
import { get } from "lodash";
import { CalendarDateInput, CalendarDateInputProps, TimePicker } from "../components";

interface DateTimeFieldProps {
  name: string;
  label?: string;
  min?: string;
  max?: string;
}

const DATE_FORMAT = "yyyy-MM-dd";

// Some call sites format datetime values with a space separator (e.g.
// "yyyy-MM-dd HH:mm") instead of the native datetime-local "T" separator, so
// both are accepted here.
function splitDateTime(value: string | undefined): [string, string] {
  if (!value) return ["", ""];
  const [datePart = "", timePart = ""] = value.split(/[T ]/);
  return [datePart, timePart];
}

function combineDateTime(datePart: string, timePart: string): string {
  if (!datePart) return "";
  return `${datePart}T${timePart || "00:00"}`;
}

// date-fns `parse` returns an Invalid Date (not null) when `datePart` doesn't
// match DATE_FORMAT; react-datepicker doesn't guard against that and throws
// deep inside its own render when it tries to format an invalid `selected`.
function parseDatePart(datePart: string): Date | null {
  if (!datePart) return null;
  const parsed = parse(datePart, DATE_FORMAT, new Date());
  return isValid(parsed) ? parsed : null;
}

/**
 * `type="datetime-local"` counterpart to `DateField`: the date portion gets
 * the same AD/BS-togglable `CalendarDateInput`, the time-of-day portion stays
 * a plain time input since BS has no separate time system. Output stays the
 * exact `"yyyy-MM-dd'T'HH:mm"` string the native datetime-local input already
 * produced, so nothing downstream changes.
 */
function DateTimeField<Value extends FormikValues>({
  name,
  min,
  max,
  allowTyping,
  ...props
}: DateTimeFieldProps &
  Omit<CalendarDateInputProps, "onChange" | "selected" | "dateFormat">) {
  const { setFieldValue, values } = useFormikContext<Value>();
  const rawValue = get(values, name, "") as string;
  const [datePart, timePart] = splitDateTime(rawValue);

  const [minDatePart, minTimePart] = splitDateTime(min);
  const [maxDatePart, maxTimePart] = splitDateTime(max);

  const selected = parseDatePart(datePart);
  const minDate = parseDatePart(minDatePart) ?? undefined;
  const maxDate = parseDatePart(maxDatePart) ?? undefined;

  return (
    <HStack width="100%" spacing={2} alignItems="flex-start">
      <CalendarDateInput
        {...props}
        allowTyping={allowTyping}
        dateFormat={DATE_FORMAT}
        minDate={minDate}
        maxDate={maxDate}
        selected={selected}
        onChange={(date) => {
          const newDatePart = date ? format(date, DATE_FORMAT) : "";
          setFieldValue(
            name,
            newDatePart ? combineDateTime(newDatePart, timePart) : "",
            true,
          );
        }}
      />
      <TimePicker
        value={timePart}
        allowTyping={allowTyping}
        minTime={datePart && datePart === minDatePart ? minTimePart : undefined}
        maxTime={datePart && datePart === maxDatePart ? maxTimePart : undefined}
        onChange={(newTimePart) => {
          setFieldValue(
            name,
            datePart ? combineDateTime(datePart, newTimePart) : "",
            true,
          );
        }}
      />
    </HStack>
  );
}
export default DateTimeField;
